import { api } from '@/lib/api/client'
import type { ChannelName, ChannelWithStats } from '@/types/channel'
import type { IChannel } from '@/types/TransmissionGroup'

/**
 * Como o `response_model` do back declara a resposta: `name`, `description` e
 * `phone_hash_id` são `string | None` (rede contra doc legado — mediram zero
 * documentos assim), e `waiting_chats` só existe a partir do commit `8fa86c72`.
 */
interface ChannelWithStatsDump
  extends Omit<ChannelWithStats, 'name' | 'description' | 'phone_hash_id' | 'waiting_chats'> {
  name: string | null
  description: string | null
  phone_hash_id: string | null
  waiting_chats?: number
}

/**
 * O `null` do contrato para AQUI. Alargar `ChannelWithStats` vazaria nulabilidade
 * pro `ScopedChannel`, que é compartilhado com `/auth/me` e não a declara — e
 * espalharia `??` por todo consumidor de `channel.name`. Um arquivo paga a rede.
 *
 * `waiting_chats` cai em `0` quando ausente porque **nenhum ambiente tem a feature
 * ainda**: em dev a rota responde sem o campo e `undefined` viraria `NaN` na tela.
 * Quando o build subir em todo lugar, o `?? 0` vira redundância inofensiva.
 */
function normalize(dump: ChannelWithStatsDump): ChannelWithStats {
  return {
    ...dump,
    name: dump.name ?? '',
    description: dump.description ?? '',
    phone_hash_id: dump.phone_hash_id ?? '',
    waiting_chats: dump.waiting_chats ?? 0,
  }
}

/** Lista escopada de canais com contadores (substitui chatbot_info.channels e available_channels_redirect). */
export async function listChannels(): Promise<ChannelWithStats[]> {
  const { data } = await api.get<ChannelWithStatsDump[]>('/whatsapp/channels')
  return data.map(normalize)
}

/** Snapshot do canal (chats + templates ∩ allowlist + TGs) — payload idêntico ao ack channel_info morto. */
export async function getChannelChats(channelId: string): Promise<IChannel> {
  const { data } = await api.get<IChannel>(`/whatsapp/channels/${channelId}/chats`)
  return data
}

/**
 * Roster do tenant: TODO canal, sem escopo, ordenado por `name` pelo back.
 *
 * Atrás da permission `channels:names:read`; sem ela o back responde 403, que o
 * interceptor rejeita como `ApiError` sem toast e sem log (`lib/api/client.ts::onResponseError`).
 * Quem gateia antes é `useChannelRosterQuery` — este service não decide permission.
 *
 * `name` é `str` NÃO-opcional no contrato do roster (projeção própria — o `str | None` do
 * `GET /whatsapp/channels` vem da projeção gorda, que esta rota não reusa). O `?? ''` fica
 * como rede de runtime, não como tratamento de caso declarado: contrato de back é promessa,
 * e nenhum consumidor de nome de canal declara nulabilidade.
 */
export async function listChannelNames(): Promise<ChannelName[]> {
  const { data } = await api.get<{ db_id: string; name: string | null }[]>(
    '/whatsapp/channels/names',
  )
  return data.map(({ db_id, name }) => ({ db_id, name: name ?? '' }))
}
